import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import MainLayout from '../../components/layout/MainLayout';
import { useApplication } from '../../hooks/useApplication';
import {
  BellIcon,
  FireIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  Cog6ToothIcon,
  BoltIcon,
  ChartBarIcon,
  ArrowPathIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import alertInstanceService, { AlertInstance } from '../../services/api/alert-instance.service';
import alertApi from '../../services/api/alert.api';
import autoAlertApi, { AutoAlertConfig, AutoAlertStatus } from '../../services/api/auto-alert.api';
import userApi from '../../services/api/user.api';
import crmIntegrationApi from '../../services/api/crm-integration.api';
import AlertFormModal from './AlertFormModal';
import CrmIntegrationSetup from './CrmIntegrationSetup';
import { usePermissions } from '../../hooks/usePermissions';
import { UserGroupIcon, CheckCircleIcon as CheckCircleSolidIcon } from '@heroicons/react/24/outline';
import Pagination from '../../components/common/Pagination';

// Metric labels for translation
const metricLabels: Record<string, { en: string; pt: string }> = {
  'response_time': { en: 'Response time', pt: 'Tempo de resposta' },
  'response_time_avg': { en: 'Avg response time', pt: 'Tempo médio de resposta' },
  'error_rate': { en: 'Error rate', pt: 'Taxa de erros' },
  'throughput': { en: 'Throughput', pt: 'Throughput' },
  'request_count': { en: 'Request count', pt: 'Quantidade de requisições' },
  'latency': { en: 'Latency', pt: 'Latência' },
  'latency_p99': { en: 'P99 Latency', pt: 'Latência P99' },
  'latency_p95': { en: 'P95 Latency', pt: 'Latência P95' },
  'cpu_usage': { en: 'CPU usage', pt: 'Uso de CPU' },
  'memory_usage': { en: 'Memory usage', pt: 'Uso de memória' },
  'disk_usage': { en: 'Disk usage', pt: 'Uso de disco' },
};

// Format alert message with i18n support
const formatAlertMessage = (message: string | undefined | null, lang: string = 'pt'): string => {
  if (!message) return '';

  // Handle structured anomaly format: anomaly:type|params...
  if (message.startsWith('anomaly:')) {
    const parts = message.split('|');
    const type = parts[0].replace('anomaly:', '');

    if (type === 'new_activity' && parts.length >= 4) {
      const [, metric, value, unit] = parts;
      const metricLabel = metricLabels[metric]?.[lang as 'en' | 'pt'] || metric;
      return lang === 'pt'
        ? `${metricLabel}: nova atividade detectada (${value}${unit})`
        : `${metricLabel}: new activity detected (${value}${unit})`;
    }

    if (type === 'deviation' && parts.length >= 7) {
      const [, metric, current, expected, deviation, unit, direction] = parts;
      const metricLabel = metricLabels[metric]?.[lang as 'en' | 'pt'] || metric;
      const dirWord = lang === 'pt'
        ? (direction === 'above' ? 'acima' : 'abaixo')
        : direction;

      return lang === 'pt'
        ? `${metricLabel} está ${deviation}% ${dirWord} do esperado (atual: ${current}${unit}, esperado: ${expected}${unit})`
        : `${metricLabel} is ${deviation}% ${dirWord} expected (current: ${current}${unit}, expected: ${expected}${unit})`;
    }
  }

  // Clean up old format messages - remove useless anomaly info
  return message
    .replace(/\s*\(expected:\s*0\.0,\s*deviation:\s*0\.0%\)/gi, '')
    .replace(/\s*\(expected:\s*0,\s*deviation:\s*0%\)/gi, '')
    .trim();
};

export default function AlertsPage() {
  const { t, i18n } = useTranslation('alerts');
  const currentLang = i18n.language?.startsWith('pt') ? 'pt' : 'en';
  const navigate = useNavigate();
  const { canCreateAlerts, canEditAlerts, canDeleteAlerts } = usePermissions();
  const { selectedAppId, availableApps } = useApplication();
  const currentApp = availableApps.find((a) => a.id === selectedAppId);
  const appId = selectedAppId || undefined;
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<any>(null);
  const [firingAlerts, setFiringAlerts] = useState<AlertInstance[]>([]);
  const [recentAlerts, setRecentAlerts] = useState<AlertInstance[]>([]);
  const [alertRules, setAlertRules] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState<'firing' | 'recent' | 'rules'>('firing');
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingRule, setEditingRule] = useState<any>(null);
  const [showConfigPanel, setShowConfigPanel] = useState(false);

  // Auto-alert state
  const [autoAlertStatus, setAutoAlertStatus] = useState<AutoAlertStatus | null>(null);
  const [autoAlertConfig, setAutoAlertConfig] = useState<AutoAlertConfig | null>(null);
  const [savingConfig, setSavingConfig] = useState(false);
  const [users, setUsers] = useState<any[]>([]);

  // CRM state
  const [showCrmSetup, setShowCrmSetup] = useState(false);
  const [crmConfigured, setCrmConfigured] = useState(false);

  // Pagination state
  const [firingPage, setFiringPage] = useState(1);
  const [firingPageSize, setFiringPageSize] = useState(10);
  const [firingTotal, setFiringTotal] = useState(0);

  const [recentPage, setRecentPage] = useState(1);
  const [recentPageSize, setRecentPageSize] = useState(10);
  const [recentTotal, setRecentTotal] = useState(0);

  const [rulesPage, setRulesPage] = useState(1);
  const [rulesPageSize, setRulesPageSize] = useState(10);
  const [rulesTotal, setRulesTotal] = useState(0);

  const getDefaultConfig = (): AutoAlertConfig => ({
    tenantId: '',
    enabled: true,
    slowQueryThresholdMs: 500,
    errorRateThresholdPercent: 5,
    responseTimeThresholdMs: 1000,
    checkIntervalMinutes: 5,
    aiAnalysisEnabled: true,
    notificationChannels: {
      email: true,
      panel: true,
      crm: {
        enabled: false,
        whatsapp: false,
        telegram: false,
        call: false,
        discord: false,
      } as any,
    },
    recipients: [],
  });

  useEffect(() => {
    loadData();
    setFiringPage(1);
    setRecentPage(1);
    setRulesPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appId]);

  // Load firing alerts with pagination
  const loadFiringAlerts = async (page: number, pageSize: number) => {
    const offset = (page - 1) * pageSize;
    const res = await alertInstanceService.getAll({ status: 'firing', application_id: appId }, pageSize, offset);
    if (res.success) {
      setFiringAlerts(res.data);
      setFiringTotal(res.meta?.total || res.data.length);
    }
  };

  // Load recent alerts with pagination
  const loadRecentAlerts = async (page: number, pageSize: number) => {
    const offset = (page - 1) * pageSize;
    const res = await alertInstanceService.getAll({ application_id: appId }, pageSize, offset);
    if (res.success) {
      setRecentAlerts(res.data);
      setRecentTotal(res.meta?.total || res.data.length);
    }
  };

  // Load rules with pagination
  const loadRules = async (page: number, pageSize: number) => {
    const offset = (page - 1) * pageSize;
    const res = await alertApi.getAll(appId, pageSize, offset);
    if (res.success) {
      setAlertRules(res.data);
      setRulesTotal(res.meta?.total || res.data.length);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [statsRes, firingRes, recentRes, rulesRes, usersRes] = await Promise.all([
        alertInstanceService.getStats(),
        alertInstanceService.getAll({ status: 'firing', application_id: appId }, firingPageSize, 0),
        alertInstanceService.getAll({ application_id: appId }, recentPageSize, 0),
        alertApi.getAll(appId, rulesPageSize, 0),
        userApi.getAll(),
      ]);

      if (statsRes.success) setStats(statsRes.data);
      if (firingRes.success) {
        setFiringAlerts(firingRes.data);
        setFiringTotal(firingRes.meta?.total || firingRes.data.length);
      }
      if (recentRes.success) {
        setRecentAlerts(recentRes.data);
        setRecentTotal(recentRes.meta?.total || recentRes.data.length);
      }
      if (rulesRes.success) {
        setAlertRules(rulesRes.data);
        setRulesTotal(rulesRes.meta?.total || rulesRes.data.length);
      }
      if (usersRes.success) setUsers(usersRes.data);

      // Load CRM integration status
      try {
        const crmRes = await crmIntegrationApi.get();
        setCrmConfigured(crmRes.success && crmRes.data !== null && crmRes.data.is_active === true);
      } catch {
        setCrmConfigured(false);
      }

      // Load auto-alert data
      try {
        console.log('[AutoAlert] Loading status and config...');
        const statusData = await autoAlertApi.getStatus().catch((err) => {
          console.error('[AutoAlert] getStatus error:', err);
          return null;
        });
        console.log('[AutoAlert] Status loaded:', statusData);

        const configData = await autoAlertApi.getConfig().catch((err) => {
          console.error('[AutoAlert] getConfig error:', err?.response?.status, err?.response?.data, err?.message);
          return null;
        });
        console.log('[AutoAlert] Config loaded:', configData);
        console.log('[AutoAlert] configData.recipients:', configData?.recipients);
        console.log('[AutoAlert] typeof recipients:', typeof configData?.recipients);
        console.log('[AutoAlert] Array.isArray(recipients):', Array.isArray(configData?.recipients));

        setAutoAlertStatus(statusData);

        // Ensure recipients is always an array
        let recipientsArray: string[] = [];
        if (configData?.recipients) {
          if (Array.isArray(configData.recipients)) {
            recipientsArray = configData.recipients;
          } else if (typeof configData.recipients === 'string') {
            // If it's a JSON string, try to parse it
            try {
              recipientsArray = JSON.parse(configData.recipients);
            } catch {
              recipientsArray = [];
            }
          }
        }
        console.log('[AutoAlert] Processed recipientsArray:', recipientsArray);

        const merged = {
          ...getDefaultConfig(),
          ...configData,
          notificationChannels: {
            ...getDefaultConfig().notificationChannels,
            ...configData?.notificationChannels,
            crm: {
              ...getDefaultConfig().notificationChannels.crm,
              ...configData?.notificationChannels?.crm,
            },
          },
          recipients: recipientsArray,
        };
        console.log('[AutoAlert] Final merged config:', merged);
        setAutoAlertConfig(merged);
      } catch (error) {
        console.error('[AutoAlert] Error loading config:', error);
        setAutoAlertConfig(getDefaultConfig());
      }
    } catch (error) {
      console.error('Error loading alerts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAutoAlertConfig = async () => {
    if (!autoAlertConfig) return;

    // Validação
    if (autoAlertConfig.recipients.length === 0) {
      toast.error(t('messages.selectAtLeastOneRecipient'));
      return;
    }

    if (!autoAlertConfig.notificationChannels?.email && !autoAlertConfig.notificationChannels?.panel && !autoAlertConfig.notificationChannels?.crm?.enabled) {
      toast.error(t('messages.selectNotificationChannel'));
      return;
    }

    try {
      setSavingConfig(true);
      console.log('[AutoAlert] Saving config with recipients:', autoAlertConfig.recipients);
      const updated = await autoAlertApi.updateConfig(autoAlertConfig);
      console.log('[AutoAlert] Save response:', updated);

      // Ensure recipients is always an array
      let recipientsArray: string[] = [];
      if (updated?.recipients) {
        if (Array.isArray(updated.recipients)) {
          recipientsArray = updated.recipients;
        } else if (typeof updated.recipients === 'string') {
          try {
            recipientsArray = JSON.parse(updated.recipients);
          } catch {
            recipientsArray = autoAlertConfig.recipients || [];
          }
        }
      } else {
        recipientsArray = autoAlertConfig.recipients || [];
      }

      // Merge com defaults para garantir que todos os campos existem
      const merged = {
        ...getDefaultConfig(),
        ...updated,
        notificationChannels: {
          ...getDefaultConfig().notificationChannels,
          ...updated?.notificationChannels,
          crm: {
            ...getDefaultConfig().notificationChannels.crm,
            ...updated?.notificationChannels?.crm,
          },
        },
        recipients: recipientsArray,
      };
      console.log('[AutoAlert] Merged after save:', merged);
      setAutoAlertConfig(merged);
      toast.success(t('autoAlert.messages.configSaved'));
      setShowConfigPanel(false);
    } catch (error: any) {
      console.error('[AutoAlert] Save error:', error);
      toast.error(error.response?.data?.error || t('autoAlert.messages.configError'));
    } finally {
      setSavingConfig(false);
    }
  };

  // Recipient management functions
  const toggleRecipient = (userId: string) => {
    if (!autoAlertConfig) return;
    const newRecipients = autoAlertConfig.recipients.includes(userId)
      ? autoAlertConfig.recipients.filter(id => id !== userId)
      : [...autoAlertConfig.recipients, userId];
    setAutoAlertConfig({ ...autoAlertConfig, recipients: newRecipients });
  };

  const selectAllUsers = () => {
    if (!autoAlertConfig) return;
    setAutoAlertConfig({ ...autoAlertConfig, recipients: users.map(u => u.id) });
  };

  const clearAllUsers = () => {
    if (!autoAlertConfig) return;
    setAutoAlertConfig({ ...autoAlertConfig, recipients: [] });
  };

  // Reload auto-alert config when panel opens
  const handleOpenConfigPanel = async () => {
    setShowConfigPanel(true);
    try {
      const configData = await autoAlertApi.getConfig();
      console.log('[AutoAlert] Panel opened - fresh config from API:', configData);

      let recipientsArray: string[] = [];
      if (configData?.recipients) {
        if (Array.isArray(configData.recipients)) {
          recipientsArray = configData.recipients;
        } else if (typeof configData.recipients === 'string') {
          try {
            recipientsArray = JSON.parse(configData.recipients);
          } catch {
            recipientsArray = [];
          }
        }
      }
      console.log('[AutoAlert] Recipients loaded:', recipientsArray);

      const merged = {
        ...getDefaultConfig(),
        ...configData,
        notificationChannels: {
          ...getDefaultConfig().notificationChannels,
          ...configData?.notificationChannels,
        },
        recipients: recipientsArray,
      };
      setAutoAlertConfig(merged);
    } catch (error) {
      console.error('[AutoAlert] Error loading config on panel open:', error);
    }
  };

  const handleAcknowledge = async (id: string) => {
    try {
      await alertInstanceService.acknowledge(id);
      toast.success(t('messages.alertAcknowledged'));
      loadData();
    } catch (error) {
      console.error('Error acknowledging alert:', error);
      toast.error(t('messages.errorAcknowledging'));
    }
  };

  const handleResolve = async (id: string) => {
    try {
      await alertInstanceService.resolve(id);
      toast.success(t('messages.alertResolved'));
      loadData();
    } catch (error) {
      console.error('Error resolving alert:', error);
      toast.error(t('messages.errorResolving'));
    }
  };

  // Ack/resolve de TODAS as ocorrências de um alerta agrupado de uma vez
  const handleAcknowledgeGroup = async (ids: string[]) => {
    try {
      await Promise.all(ids.map((id) => alertInstanceService.acknowledge(id)));
      toast.success(t('messages.alertAcknowledged'));
      loadData();
    } catch (error) {
      toast.error(t('messages.errorAcknowledging'));
    }
  };
  const handleResolveGroup = async (ids: string[]) => {
    try {
      await Promise.all(ids.map((id) => alertInstanceService.resolve(id)));
      toast.success(t('messages.alertResolved'));
      loadData();
    } catch (error) {
      toast.error(t('messages.errorResolving'));
    }
  };

  // Agrupa ocorrências repetidas do mesmo alerta (mesma regra + app) p/ tirar a poluição
  const groupAlerts = (alerts: AlertInstance[]) => {
    const map = new Map<string, { rep: AlertInstance; ids: string[]; count: number; latest: string }>();
    for (const a of alerts) {
      const key = `${a.alert_rule_id}|${a.application_id || ''}`;
      const g = map.get(key);
      if (!g) {
        map.set(key, { rep: a, ids: [a.id], count: 1, latest: a.triggered_at });
      } else {
        g.ids.push(a.id);
        g.count += 1;
        if (new Date(a.triggered_at) > new Date(g.latest)) { g.latest = a.triggered_at; g.rep = a; }
      }
    }
    return Array.from(map.values());
  };

  // Link "ver ocorrência" — manda pra seção real (APM/logs/erros) do que disparou
  const getOccurrenceLink = (alert: AlertInstance): string | null => {
    const type = `${alert.alert_rule?.alert_type || ''} ${alert.source || ''} ${alert.alert_rule?.name || ''}`.toLowerCase();
    const appId = alert.application_id;
    const q = appId ? `?application_id=${appId}` : '';
    if (/log/.test(type)) return `/logs${q}`;
    if (/error|crash|exception/.test(type)) return `/apm/errors${q}`;
    if (/uptime|ping|disponib/.test(type)) return `/uptime`;
    if (/cpu|memory|mem|disk|metric/.test(type)) return `/metrics${q}`;
    if (/trace|apm|throughput|latency|latencia|response|requisi|transaction/.test(type)) return `/apm${q}`;
    return `/apm${q}`;
  };

  const handleDeleteRule = async (id: string) => {
    toast((toastInstance) => (
      <div className="flex flex-col space-y-3">
        <p className="font-medium text-white">{t('messages.confirmDelete')}</p>
        <div className="flex space-x-2">
          <button
            onClick={async () => {
              toast.dismiss(toastInstance.id);
              try {
                await alertApi.delete(id);
                toast.success(t('messages.alertDeleted'));
                loadData();
              } catch (error) {
                console.error('Error deleting rule:', error);
                toast.error(t('messages.errorDeletingAlert'));
              }
            }}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
          >
            {t('common:buttons.delete')}
          </button>
          <button
            onClick={() => toast.dismiss(toastInstance.id)}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-500 transition-colors text-sm font-medium"
          >
            {t('common:buttons.cancel')}
          </button>
        </div>
      </div>
    ), {
      duration: 10000,
      position: 'top-center',
    });
  };

  const handleEditRule = (rule: any) => {
    setEditingRule(rule);
    setShowFormModal(true);
  };

  const handleCreateRule = () => {
    setEditingRule(null);
    setShowFormModal(true);
  };

  const handleFormClose = () => {
    setShowFormModal(false);
    setEditingRule(null);
    loadData();
  };

  // Pagination handlers
  const handleFiringPageChange = (page: number) => {
    setFiringPage(page);
    loadFiringAlerts(page, firingPageSize);
  };

  const handleFiringPageSizeChange = (pageSize: number) => {
    setFiringPageSize(pageSize);
    setFiringPage(1);
    loadFiringAlerts(1, pageSize);
  };

  const handleRecentPageChange = (page: number) => {
    setRecentPage(page);
    loadRecentAlerts(page, recentPageSize);
  };

  const handleRecentPageSizeChange = (pageSize: number) => {
    setRecentPageSize(pageSize);
    setRecentPage(1);
    loadRecentAlerts(1, pageSize);
  };

  const handleRulesPageChange = (page: number) => {
    setRulesPage(page);
    loadRules(page, rulesPageSize);
  };

  const handleRulesPageSizeChange = (pageSize: number) => {
    setRulesPageSize(pageSize);
    setRulesPage(1);
    loadRules(1, pageSize);
  };

  const getSeverityBg = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'bg-gradient-to-r from-red-500 to-rose-600';
      case 'warning':
        return 'bg-gradient-to-r from-yellow-500 to-orange-600';
      case 'info':
        return 'bg-gradient-to-r from-blue-500 to-indigo-600';
      default:
        return 'bg-gradient-to-r from-gray-500 to-gray-600';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'firing':
        return 'text-red-600 bg-red-100 dark:bg-red-900/30 dark:text-red-400';
      case 'acknowledged':
        return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'resolved':
        return 'text-green-600 bg-green-100 dark:bg-green-900/30 dark:text-green-400';
      default:
        return 'text-gray-600 bg-gray-100 dark:bg-gray-700 dark:text-gray-400';
    }
  };

  const getRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return t('timeAgo.justNow', 'agora');
    if (diffMins < 60) return t('timeAgo.minutesAgo', { count: diffMins }, `${diffMins}m atrás`);
    if (diffHours < 24) return t('timeAgo.hoursAgo', { count: diffHours }, `${diffHours}h atrás`);
    return t('timeAgo.daysAgo', { count: diffDays }, `${diffDays}d atrás`);
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-red-500 to-rose-600 shadow-lg shadow-red-500/20">
              <BellIcon className="h-6 w-6 text-white" />
            </div>
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                {t('dashboard.title')}
              </h1>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <span className="hidden text-sm text-gray-500 dark:text-gray-400 sm:inline">
                  {t('dashboard.subtitle')}
                </span>
                {/* Contexto: qual aplicação está sendo exibida */}
                <span className="inline-flex items-center gap-1.5 rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-medium text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300">
                  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                  {currentApp?.name || t('dashboard.allApplications', 'Todas as aplicações')}
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowCrmSetup(true)}
              className={`flex items-center justify-center space-x-2 px-4 py-2.5 rounded-xl transition-all ${
                crmConfigured
                  ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 hover:bg-green-200 dark:hover:bg-green-900/50'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              <span className="hidden sm:inline font-medium">{t('crm.title')}</span>
            </button>
            <button
              onClick={handleOpenConfigPanel}
              className="flex items-center justify-center space-x-2 px-4 py-2.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-all"
            >
              <Cog6ToothIcon className="h-5 w-5" />
              <span className="hidden sm:inline font-medium">{t('autoAlert.config')}</span>
            </button>
            {canCreateAlerts && (
              <button
                onClick={handleCreateRule}
                className="flex items-center justify-center space-x-2 px-4 sm:px-6 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl hover:from-indigo-700 hover:to-purple-700 transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                <PlusIcon className="h-5 w-5" />
                <span className="font-semibold text-sm sm:text-base">{t('createAlert')}</span>
              </button>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
          <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
            <div className="flex items-center justify-between">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-red-50 dark:bg-red-900/20">
                <FireIcon className="h-5 w-5 text-red-600 dark:text-red-400" />
              </div>
              <span className={`text-2xl font-bold ${firingTotal > 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'}`}>{firingTotal}</span>
            </div>
            <h3 className="mt-3 text-sm font-medium text-gray-500 dark:text-gray-400">{t('dashboard.firingAlerts')}</h3>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
            <div className="flex items-center justify-between">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-green-50 dark:bg-green-900/20">
                <CheckCircleIcon className="h-5 w-5 text-green-600 dark:text-green-400" />
              </div>
              <span className="text-2xl font-bold text-gray-900 dark:text-white">{stats?.resolved_today || 0}</span>
            </div>
            <h3 className="mt-3 text-sm font-medium text-gray-500 dark:text-gray-400">{t('dashboard.resolvedToday')}</h3>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
            <div className="flex items-center justify-between">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-50 dark:bg-amber-900/20">
                <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 dark:text-amber-400" />
              </div>
              <span className="text-2xl font-bold text-gray-900 dark:text-white">{stats?.acknowledged || 0}</span>
            </div>
            <h3 className="mt-3 text-sm font-medium text-gray-500 dark:text-gray-400">{t('dashboard.acknowledgedAlerts')}</h3>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
            <div className="flex items-center justify-between">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-50 dark:bg-indigo-900/20">
                <ClockIcon className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
              </div>
              <span className="text-2xl font-bold text-gray-900 dark:text-white">{alertRules.length}</span>
            </div>
            <h3 className="mt-3 text-sm font-medium text-gray-500 dark:text-gray-400">{t('dashboard.totalRules')}</h3>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white dark:bg-gray-800 rounded-xl sm:rounded-2xl shadow-lg overflow-hidden">
          <div className="border-b border-gray-200 dark:border-gray-700">
            <div className="flex justify-around sm:justify-start sm:space-x-8 px-2 sm:px-6">
              <button
                onClick={() => setActiveTab('firing')}
                className={`py-3 sm:py-4 px-2 sm:px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'firing'
                    ? 'border-red-500 text-red-600 dark:text-red-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
                }`}
              >
                <div className="flex items-center space-x-1 sm:space-x-2">
                  <FireIcon className="h-5 w-5" />
                  <span className="hidden sm:inline">{t('dashboard.firingAlerts')}</span>
                  {firingTotal > 0 && (
                    <span className="px-1.5 sm:px-2 py-0.5 text-xs font-bold rounded-full bg-red-100 text-red-600 dark:bg-red-900/30">
                      {firingTotal}
                    </span>
                  )}
                </div>
              </button>

              <button
                onClick={() => setActiveTab('recent')}
                className={`py-3 sm:py-4 px-2 sm:px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'recent'
                    ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
                }`}
              >
                <div className="flex items-center space-x-1 sm:space-x-2">
                  <ClockIcon className="h-5 w-5" />
                  <span className="hidden sm:inline">{t('recentAlerts')}</span>
                </div>
              </button>

              <button
                onClick={() => setActiveTab('rules')}
                className={`py-3 sm:py-4 px-2 sm:px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'rules'
                    ? 'border-purple-500 text-purple-600 dark:text-purple-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
                }`}
              >
                <div className="flex items-center space-x-1 sm:space-x-2">
                  <BellIcon className="h-5 w-5" />
                  <span className="hidden sm:inline">{t('alertRules')}</span>
                </div>
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            {activeTab === 'firing' && (
              <div>
                {firingAlerts.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 mb-4">
                      <CheckCircleIcon className="h-8 w-8 text-green-600 dark:text-green-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                      {t('dashboard.noFiringAlerts')}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">{t('dashboard.allClear')}</p>
                  </div>
                ) : (
                  <>
                    <div className="space-y-3">
                      {groupAlerts(firingAlerts).map((g) => {
                        const alert = g.rep;
                        const link = getOccurrenceLink(alert);
                        return (
                        <div
                          key={`${alert.alert_rule_id}|${alert.application_id || ''}`}
                          className="rounded-xl border border-gray-200 bg-white p-4 transition-shadow hover:shadow-sm dark:border-gray-700 dark:bg-gray-800/50"
                        >
                          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                            <div className="min-w-0 flex-1">
                              <div className="mb-1.5 flex flex-wrap items-center gap-2">
                                <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-bold ${getSeverityBg(alert.alert_rule?.severity || 'info')} text-white`}>
                                  {t(`severities.${alert.alert_rule?.severity}`)}
                                </span>
                                {(alert as any).application?.name && (
                                  <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-700/60 dark:text-gray-300">
                                    <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" /></svg>
                                    {(alert as any).application.name}
                                  </span>
                                )}
                                {g.count > 1 && (
                                  <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-bold text-red-700 dark:bg-red-900/40 dark:text-red-300" title={t('occurrencesTooltip', 'Ocorrências repetidas deste mesmo alerta')}>
                                    ×{g.count}
                                  </span>
                                )}
                                <span className="text-xs text-gray-400">{getRelativeTime(g.latest)}</span>
                              </div>
                              <h3 className="break-words text-base font-semibold text-gray-900 dark:text-white">
                                {alert.alert_rule?.name}
                              </h3>
                              {alert.alert_rule?.description && (
                                <p className="mt-0.5 text-xs text-gray-400 dark:text-gray-500">{alert.alert_rule.description}</p>
                              )}
                              {formatAlertMessage(alert.message, currentLang) && (
                                <p className="mt-1 break-words text-sm text-gray-600 dark:text-gray-300">
                                  {formatAlertMessage(alert.message, currentLang)}
                                </p>
                              )}
                              {link && (
                                <button onClick={() => navigate(link)} className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-indigo-600 hover:text-indigo-700 dark:text-indigo-400">
                                  {t('viewOccurrence', 'Ver ocorrência')}
                                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" /></svg>
                                </button>
                              )}
                            </div>
                            <div className="flex flex-shrink-0 gap-2">
                              <button
                                onClick={() => handleAcknowledgeGroup(g.ids)}
                                title={t('actions.acknowledgeHint', 'Marcar como visto (para de notificar; o problema continua)')}
                                className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                              >
                                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                                <span className="hidden sm:inline">{t('actions.acknowledge')}</span>
                              </button>
                              <button
                                onClick={() => handleResolveGroup(g.ids)}
                                title={t('actions.resolveHint', 'Marcar como resolvido (o problema foi corrigido)')}
                                className="inline-flex items-center gap-1.5 rounded-lg bg-green-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-green-700"
                              >
                                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                                <span className="hidden sm:inline">{t('actions.resolve')}</span>
                              </button>
                            </div>
                          </div>
                        </div>
                        );
                      })}
                    </div>
                    {firingTotal > 0 && (
                      <div className="mt-4">
                        <Pagination
                          currentPage={firingPage}
                          totalItems={firingTotal}
                          itemsPerPage={firingPageSize}
                          onPageChange={handleFiringPageChange}
                          onItemsPerPageChange={handleFiringPageSizeChange}
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {activeTab === 'recent' && (
              <div>
                <div className="space-y-4">
                  {recentAlerts.map((alert) => (
                    <div
                      key={alert.id}
                      className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 hover:shadow-lg transition-all"
                    >
                      <div className="flex items-center space-x-3 mb-2">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold ${getStatusColor(alert.status)}`}>
                          {t(`statuses.${alert.status}`)}
                        </span>
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold ${getSeverityBg(alert.alert_rule?.severity || 'info')} text-white`}>
                          {t(`severities.${alert.alert_rule?.severity}`)}
                        </span>
                        <span className="text-xs text-gray-500">{getRelativeTime(alert.triggered_at)}</span>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{alert.alert_rule?.name}</h3>
                      {formatAlertMessage(alert.message, currentLang) && <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{formatAlertMessage(alert.message, currentLang)}</p>}
                    </div>
                  ))}
                </div>
                {recentTotal > 0 && (
                  <div className="mt-4">
                    <Pagination
                      currentPage={recentPage}
                      totalItems={recentTotal}
                      itemsPerPage={recentPageSize}
                      onPageChange={handleRecentPageChange}
                      onItemsPerPageChange={handleRecentPageSizeChange}
                    />
                  </div>
                )}
              </div>
            )}

            {activeTab === 'rules' && (
              <div>
                <div className="space-y-4">
                  {alertRules.map((rule) => (
                    <div
                      key={rule.id}
                      className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 sm:p-6 hover:shadow-lg transition-all"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2 mb-2">
                            <span className={`inline-flex items-center px-2 sm:px-3 py-1 rounded-full text-xs font-bold ${getSeverityBg(rule.severity)} text-white`}>
                              {t(`severities.${rule.severity}`)}
                            </span>
                            <span className={`px-2 py-1 rounded-md text-xs font-medium ${
                              rule.status === 'active' ? 'bg-green-100 text-green-700 dark:bg-green-900/30' : 'bg-gray-100 text-gray-700 dark:bg-gray-700'
                            }`}>
                              {t(`statuses.${rule.status}`)}
                            </span>
                          </div>
                          <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white break-words">{rule.name}</h3>
                          {rule.description && <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 break-words">{rule.description}</p>}
                          <div className="flex items-center space-x-4 mt-3 text-xs sm:text-sm text-gray-500">
                            <span>{t('rules.triggered')} {rule.trigger_count} {t('rules.times')}</span>
                          </div>
                        </div>
                        {(canEditAlerts || canDeleteAlerts) && (
                          <div className="flex items-center space-x-2 flex-shrink-0">
                            {canEditAlerts && (
                              <button
                                onClick={() => handleEditRule(rule)}
                                className="p-2 text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 rounded-lg transition-colors"
                              >
                                <PencilIcon className="h-5 w-5" />
                              </button>
                            )}
                            {canDeleteAlerts && (
                              <button
                                onClick={() => handleDeleteRule(rule.id)}
                                className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                              >
                                <TrashIcon className="h-5 w-5" />
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                {rulesTotal > 0 && (
                  <div className="mt-4">
                    <Pagination
                      currentPage={rulesPage}
                      totalItems={rulesTotal}
                      itemsPerPage={rulesPageSize}
                      onPageChange={handleRulesPageChange}
                      onItemsPerPageChange={handleRulesPageSizeChange}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Config Slide Panel */}
      {showConfigPanel && autoAlertConfig && (
        <div className="fixed inset-0 z-50 overflow-hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowConfigPanel(false)} />
          <div className="absolute inset-y-0 right-0 w-full max-w-lg bg-white dark:bg-gray-800 shadow-xl">
            <div className="h-full flex flex-col">
              {/* Panel Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-3">
                  <BoltIcon className="w-6 h-6 text-yellow-500" />
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{t('autoAlert.title')}</h2>
                </div>
                <button onClick={() => setShowConfigPanel(false)} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
                  <XMarkIcon className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              {/* Panel Content */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {/* Auto-Alerts Toggle */}
                <div className="flex items-center justify-between p-3 bg-yellow-50 dark:bg-yellow-900/10 rounded-lg">
                  <div>
                    <h3 className="font-medium text-gray-900 dark:text-white text-sm">{t('autoAlert.enableAutoAlerts')}</h3>
                    <p className="text-xs text-gray-500">{t('autoAlert.enableAutoAlertsDesc')}</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={autoAlertConfig.enabled}
                      onChange={(e) => setAutoAlertConfig({ ...autoAlertConfig, enabled: e.target.checked })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-yellow-500"></div>
                  </label>
                </div>

                {/* Thresholds */}
                <div className="space-y-3">
                  <h3 className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                    <ChartBarIcon className="w-5 h-5 text-blue-500" />
                    {t('autoAlert.thresholds.title')}
                  </h3>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-medium text-gray-600 dark:text-gray-400">{t('autoAlert.thresholds.slowQuery')}</label>
                      <input
                        type="number"
                        value={autoAlertConfig.slowQueryThresholdMs}
                        onChange={(e) => setAutoAlertConfig({ ...autoAlertConfig, slowQueryThresholdMs: parseInt(e.target.value) || 500 })}
                        className="w-full mt-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-600 dark:text-gray-400">{t('autoAlert.thresholds.errorRate')}</label>
                      <input
                        type="number"
                        value={autoAlertConfig.errorRateThresholdPercent}
                        onChange={(e) => setAutoAlertConfig({ ...autoAlertConfig, errorRateThresholdPercent: parseFloat(e.target.value) || 5 })}
                        step={0.1}
                        className="w-full mt-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-600 dark:text-gray-400">{t('autoAlert.thresholds.responseTime')}</label>
                      <input
                        type="number"
                        value={autoAlertConfig.responseTimeThresholdMs}
                        onChange={(e) => setAutoAlertConfig({ ...autoAlertConfig, responseTimeThresholdMs: parseInt(e.target.value) || 1000 })}
                        className="w-full mt-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-600 dark:text-gray-400">{t('autoAlert.thresholds.checkInterval')}</label>
                      <input
                        type="number"
                        value={autoAlertConfig.checkIntervalMinutes}
                        onChange={(e) => setAutoAlertConfig({ ...autoAlertConfig, checkIntervalMinutes: parseInt(e.target.value) || 5 })}
                        className="w-full mt-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>
                  </div>
                </div>

                {/* Notification Channels */}
                <div className="space-y-3">
                  <h3 className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                    <BellIcon className="w-5 h-5 text-purple-500" />
                    {t('autoAlert.notifications.title')}
                  </h3>
                  <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg space-y-2">
                    <label className="flex items-center space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={autoAlertConfig.notificationChannels?.email ?? true}
                        onChange={(e) => setAutoAlertConfig({
                          ...autoAlertConfig,
                          notificationChannels: {
                            ...autoAlertConfig.notificationChannels,
                            email: e.target.checked,
                          },
                        })}
                        className="w-5 h-5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        {t('fields.enableEmail')}
                      </span>
                    </label>
                    <label className="flex items-center space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={autoAlertConfig.notificationChannels?.panel ?? true}
                        onChange={(e) => setAutoAlertConfig({
                          ...autoAlertConfig,
                          notificationChannels: {
                            ...autoAlertConfig.notificationChannels,
                            panel: e.target.checked,
                          },
                        })}
                        className="w-5 h-5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        {t('fields.enablePanel')}
                      </span>
                    </label>
                  </div>
                </div>

                {/* CRM Channels */}
                <div className="space-y-3">
                  <h3 className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                    <svg className="w-5 h-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                    {t('crm.integration')}
                  </h3>

                  {crmConfigured ? (
                    <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg space-y-2">
                      <div className="flex items-center justify-between">
                        <label className="flex items-center space-x-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={autoAlertConfig.notificationChannels?.crm?.enabled ?? false}
                            onChange={(e) => setAutoAlertConfig({
                              ...autoAlertConfig,
                              notificationChannels: {
                                ...autoAlertConfig.notificationChannels,
                                crm: {
                                  ...autoAlertConfig.notificationChannels?.crm,
                                  enabled: e.target.checked,
                                },
                              },
                            })}
                            className="w-5 h-5 rounded border-gray-300 text-green-600 focus:ring-green-500"
                          />
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            {t('crm.channels.title')}
                          </span>
                        </label>
                        <button
                          type="button"
                          onClick={() => { setShowConfigPanel(false); setShowCrmSetup(true); }}
                          className="flex items-center gap-1 text-xs px-2 py-1 bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 rounded-lg hover:bg-green-200 dark:hover:bg-green-900/50 transition-colors"
                        >
                          <Cog6ToothIcon className="w-3.5 h-3.5" />
                          {t('crm.configure')}
                        </button>
                      </div>

                      {autoAlertConfig.notificationChannels?.crm?.enabled && (
                        <div className="ml-8 space-y-2 pt-1">
                          <label className="flex items-center space-x-3 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={autoAlertConfig.notificationChannels?.crm?.whatsapp ?? false}
                              onChange={(e) => setAutoAlertConfig({
                                ...autoAlertConfig,
                                notificationChannels: {
                                  ...autoAlertConfig.notificationChannels,
                                  crm: {
                                    ...autoAlertConfig.notificationChannels?.crm,
                                    whatsapp: e.target.checked,
                                  },
                                },
                              })}
                              className="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                            />
                            <span className="text-sm text-gray-700 dark:text-gray-300">
                              {t('crm.channels.whatsapp')}
                            </span>
                          </label>
                          <label className="flex items-center space-x-3 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={autoAlertConfig.notificationChannels?.crm?.telegram ?? false}
                              onChange={(e) => setAutoAlertConfig({
                                ...autoAlertConfig,
                                notificationChannels: {
                                  ...autoAlertConfig.notificationChannels,
                                  crm: {
                                    ...autoAlertConfig.notificationChannels?.crm,
                                    telegram: e.target.checked,
                                  },
                                },
                              })}
                              className="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                            />
                            <span className="text-sm text-gray-700 dark:text-gray-300">
                              {t('crm.channels.telegram')}
                            </span>
                          </label>
                          <label className="flex items-center space-x-3 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={(autoAlertConfig.notificationChannels?.crm as any)?.discord ?? false}
                              onChange={(e) => setAutoAlertConfig({
                                ...autoAlertConfig,
                                notificationChannels: {
                                  ...autoAlertConfig.notificationChannels,
                                  crm: {
                                    ...autoAlertConfig.notificationChannels?.crm,
                                    discord: e.target.checked,
                                  } as any,
                                },
                              })}
                              className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                            />
                            <span className="text-sm text-gray-700 dark:text-gray-300">
                              {t('crm.channels.discord', 'Discord')}
                            </span>
                          </label>
                          <label className="flex items-center space-x-3 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={autoAlertConfig.notificationChannels?.crm?.call ?? false}
                              onChange={(e) => setAutoAlertConfig({
                                ...autoAlertConfig,
                                notificationChannels: {
                                  ...autoAlertConfig.notificationChannels,
                                  crm: {
                                    ...autoAlertConfig.notificationChannels?.crm,
                                    call: e.target.checked,
                                  },
                                },
                              })}
                              className="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                            />
                            <span className="text-sm text-gray-700 dark:text-gray-300">
                              {t('crm.channels.call')}
                            </span>
                          </label>
                          <p className="text-xs text-gray-500 dark:text-gray-400 italic mt-1">
                            {t('crm.phonesFromConfig')}
                          </p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                        {t('crm.notConfigured')}
                      </p>
                      <button
                        type="button"
                        onClick={() => { setShowConfigPanel(false); setShowCrmSetup(true); }}
                        className="flex items-center gap-1.5 text-sm px-3 py-1.5 bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400 rounded-lg hover:bg-indigo-200 dark:hover:bg-indigo-900/50 transition-colors font-medium"
                      >
                        <Cog6ToothIcon className="w-4 h-4" />
                        {t('crm.configure')}
                      </button>
                    </div>
                  )}
                </div>

                {/* Recipients */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                      <UserGroupIcon className="w-5 h-5 text-green-500" />
                      {t('fields.recipients')} ({autoAlertConfig.recipients?.length || 0})
                    </h3>
                    <div className="flex space-x-2">
                      <button
                        type="button"
                        onClick={selectAllUsers}
                        className="text-xs px-2 py-1 bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400 rounded-lg hover:bg-indigo-200 dark:hover:bg-indigo-900/50 transition-colors"
                      >
                        {t('common:buttons.selectAll', 'Todos')}
                      </button>
                      <button
                        type="button"
                        onClick={clearAllUsers}
                        className="text-xs px-2 py-1 bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                      >
                        {t('common:buttons.clear', 'Limpar')}
                      </button>
                    </div>
                  </div>

                  <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-2 bg-white dark:bg-gray-700 max-h-48 overflow-y-auto space-y-1">
                    {users.length === 0 ? (
                      <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                        {t('common:messages.noResults', 'Nenhum usuário encontrado')}
                      </p>
                    ) : (
                      users.map(user => (
                        <label
                          key={user.id}
                          className={`flex items-center space-x-2 p-2 rounded-lg cursor-pointer transition-all ${
                            autoAlertConfig.recipients?.includes(user.id)
                              ? 'bg-indigo-50 dark:bg-indigo-900/30 border border-indigo-500'
                              : 'hover:bg-gray-50 dark:hover:bg-gray-600 border border-transparent'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={autoAlertConfig.recipients?.includes(user.id) || false}
                            onChange={() => toggleRecipient(user.id)}
                            className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                              {user.first_name} {user.last_name}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                              {user.email}
                            </p>
                          </div>
                        </label>
                      ))
                    )}
                  </div>

                  {autoAlertConfig.recipients?.length === 0 && (
                    <p className="text-xs text-red-600 dark:text-red-400">
                      {t('fields.noRecipientsSelected')}
                    </p>
                  )}
                </div>

              </div>

              {/* Panel Footer */}
              <div className="p-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={handleSaveAutoAlertConfig}
                  disabled={savingConfig}
                  className="w-full px-4 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50 flex items-center justify-center gap-2 font-semibold"
                >
                  {savingConfig ? (
                    <>
                      <ArrowPathIcon className="w-5 h-5 animate-spin" />
                      {t('autoAlert.saving')}
                    </>
                  ) : (
                    <>
                      <CheckCircleIcon className="w-5 h-5" />
                      {t('autoAlert.saveConfig')}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Form Modal */}
      {showFormModal && (
        <AlertFormModal
          alertRule={editingRule}
          onClose={handleFormClose}
        />
      )}

      {/* CRM Integration Setup Modal */}
      {showCrmSetup && (
        <CrmIntegrationSetup
          onClose={() => setShowCrmSetup(false)}
          onSaved={() => {
            crmIntegrationApi.get().then(res => {
              setCrmConfigured(res.success && res.data !== null && res.data.is_active === true);
            }).catch(() => setCrmConfigured(false));
          }}
        />
      )}
    </MainLayout>
  );
}
